import React, { useState, useEffect, useRef } from 'react';
import './FeedbackModal.css';
import { API_BASE_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';

const FeedbackModal = ({ onClose }) => {
  // Call useAuth inside the component to get both user and authFetch.
  const { user, authFetch } = useAuth();

  // Retrieve user details from AuthContext or fallback to guest info from localStorage.
  const isLoggedIn = user && user.email;
  const user_id = isLoggedIn ? user.email : (localStorage.getItem("guestId") || "guest");
  const name = isLoggedIn ? user.name || "" : (localStorage.getItem("guestName") || "");
  const mobile = isLoggedIn
    ? user.mobile || localStorage.getItem("userMobile") || ""
    : (localStorage.getItem("guestMobile") || "");

  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Separate error states for rating and comment
  const [ratingError, setRatingError] = useState("");
  const [commentError, setCommentError] = useState("");
  const [submissionError, setSubmissionError] = useState("");

  // Optional: Auto-focus the comment box for faster typing
  const commentRef = useRef(null);
  useEffect(() => {
    if (commentRef.current) {
      commentRef.current.focus();
    }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Reset errors before validation
    setRatingError("");
    setCommentError("");
    setSubmissionError("");

    // Validate rating
    if (rating === 0) {
      setRatingError("Please select a rating.");
    }

    // Validate comment length
    if (comment.trim().length < 20) {
      setCommentError("Feedback must be at least 20 characters.");
    }

    // If any error is present, don't proceed with submission
    if (rating === 0 || comment.trim().length < 20) {
      return;
    }

    const payload = {
      user_id,
      name,
      mobile,
      rating,
      comment,
      feedback_type: "overall" // indicates overall feedback
    };

    authFetch(`${API_BASE_URL}/api/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(response => {
        if (!response.ok) {
          throw new Error("Sorry! We are facing some technical issue. Please provide feedback after sometime. Thanks");
        }
        return response.json();
      })
      .then(data => {
        setSubmitSuccess(true);
        // Optionally close modal after a delay
        setTimeout(() => onClose(), 3000);
      })
      .catch(err => {
        console.error("Feedback submission error:", err);
        setSubmissionError(
          err.message || "Sorry! We are facing some technical issue. Please provide feedback after sometime. Thanks"
        );
      });
  };

  return (
    <div className="feedback-modal">
      <div className="feedback-modal-content">
        {/* Circular Cancel button (top-right) */}
        <button className="cancel-feedback" onClick={onClose}>
          &times;
        </button>

        {submitSuccess ? (
          <div className="feedback-success">
            <h2><span style={{ color: 'green' }}>✔</span> Thank you for your valuable feedback!</h2>
            <p>We appreciate your time. This window will close automatically.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="feedback-form">
            <h2>We Value Your Feedback</h2>
            <p className="feedback-subheading">
              Please rate your overall experience and share your comments.
            </p>

            {/* Star rating block */}
            <div className="star-rating">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className={star <= (hover || rating) ? "on" : "off"}
                  onClick={() => {
                    setRating(star);
                    if (star > 0) setRatingError("");
                  }}
                  onMouseEnter={() => setHover(star)}
                  onMouseLeave={() => setHover(0)}
                >
                  <span className="star">&#9733;</span>
                </button>
              ))}
            </div>
            {/* Show rating error if any */}
            {ratingError && <p className="error-message">{ratingError}</p>}

            {/* Comment box */}
            <textarea
              ref={commentRef}
              placeholder="Share more details about your experience..."
              value={comment}
              onChange={(e) => {
                setComment(e.target.value);
                // Clear error if comment length is valid
                if (e.target.value.trim().length >= 20) {
                  setCommentError("");
                }
              }}
              maxLength={500}
              className={commentError ? "error-textarea" : ""}
            ></textarea>
            <div className="char-count">{comment.length} / 500</div>
            {/* Show comment error if any */}
            {commentError && <p className="error-message">{commentError}</p>}

            {/* Submission error (e.g., network or server error) */}
            {submissionError && <p className="error-message">{submissionError}</p>}

            <button type="submit" className="submit-feedback">
              Submit Feedback
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default FeedbackModal;
